import {useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import axios from 'axios';
import Modal from 'react-modal';

import Icon from './Icon';
import Select from '../select/Select';
import {changeStatus, deleteMicroservice, updateMicroservice} from '../../features/microservices.slice';
import _axios from '../../utils/axios';
import _kc from '../../utils/keycloak';
import {ADMIN, GROUP_ADMIN, DEFAULT_GROUP, DEFAULT_STATUS, USER} from '../../utils/settings';
import {getDateTime, handleError} from '../../utils/utils';
import circleCultured from '../../assets/circle-solid-cultured-100.png';
import circleOceanGreen from '../../assets/circle-solid-ocean-green-100.png';
import circleJellyBean from '../../assets/circle-solid-jelly-bean-100.png';
import editImg from '../../assets/pen-solid-cultured-50.png';
import trashImg from '../../assets/trash-solid-cultured-50.png';

import buttonStyles from '../../styles/base_components/Button.module.scss';
import styles from '../../styles/base_components/Table.module.scss';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(40, 40, 64, 0.5)'
  },
  content: {
    width: '80vw',
    minHeight: '50vh',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    border: 'none',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#303253'
  },
};

/**
 * @param {string} props.group
 * @param {Object} props.context
 * @param {string} props.id
 * @param {Object} props.microservice
 */
function Table(props) {
  const [microservice, setMicroservice] = useState({});
  const [deletePermission, setDeletePermission] = useState(false);

  // edit microservice
  const [modal, setModal] = useState(false);
  const [versions, setVersions] = useState({});
  const [placeholder, setPlaceholder] = useState('');
  const [multiselectError, setMultiselectError] = useState('');

  const [groups, setGroups] = useState({});
  const [gPlaceholder, setGPlaceholder] = useState('');
  const [gError, setGError] = useState('');

  const dispatch = useDispatch();
  const context = useContext(props.context);

  const colors = [-1, 0, 1];
  const dbPath = isAdmin() ? ADMIN.MICROSERVICES_PATH : isRegularUser() ? USER.MICROSERVICES_PATH : GROUP_ADMIN.MICROSERVICES_PATH;

  function isAdmin() {
    return props.group === DEFAULT_GROUP;
  }

  function isGroupAdmin() {
    return props.group !== undefined && props.group !== DEFAULT_GROUP;
  }

  function isRegularUser() {
    return props.group === undefined;
  }

  function onChangeMicroserviceStatus() {
    const i = props.microservice.status + 1;
    const next = colors[(i + 1) % 3];

    let data = {
      id: props.id,
      status: next,
      fetchers: props.microservice.fetchers.map(fetcher => ({...fetcher, status: next}))
    };
    if (isGroupAdmin()) data.group = props.group;

    _axios.put(dbPath, data)
      .then(response => {  // TODO ????
        dispatch(changeStatus({
          id: props.id,
          status: next
        }));
      })
      .catch(handleError);
  }

  function onChangeFetcherStatus(index) {
    const i = props.microservice.fetchers[index].status + 1;
    const next = colors[(i + 1) % 3];

    const newFetchers = [...props.microservice.fetchers];
    newFetchers[index] = {
      ...props.microservice.fetchers[index],
      status: next
    };
    let data = {
      id: props.id,
      fetchers: newFetchers
    };
    if (isGroupAdmin()) data.group = props.group;

    _axios.put(dbPath, data)
      .then(response => {  // TODO ???
        dispatch(changeStatus({
          id: props.id,
          fetcherIndex: index,
          status: next
        }));
      })
      .catch(handleError);
  }

  function onEditMicroservice(event) {
    event.stopPropagation();

    let url = props.microservice.host;
    axios.get(url)
      .then(response => {  // GET microservice
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('This microservice does not correspond to our standard anymore.');
        if (ok !== 1) throw new Error('This microservice is currently unavailable.');

        const path = data.find(object => {
          const {name, path} = object;
          return typeof name === 'string' && typeof path === 'string' && name.toLowerCase() === 'api';
        });
        if (!path) throw new Error('There is no available path to the microservice.');

        url = props.microservice.host + path.path;
        return axios.get(url);
      })
      .then(response => {  // GET microservice/api
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The api ' + url + ' does not correspond to our standard anymore.');
        if (ok !== 1) throw new Error('The api ' + url + ' is currently unavailable.');
        if (data.length === 0) throw new Error('The api ' + url + ' has no available version.');

        setMicroservice({
          path: url,
          versions: data
        });
        let newVersions = {};
        for (let v of data) {
          newVersions[v.name] = (v.name === props.microservice.version);
        }
        setVersions(newVersions);
        setPlaceholder(props.microservice.version);
      })
      .catch(handleErrorOnEdit);

    let groups = {};
    if (context.defaultGroup === DEFAULT_GROUP) {
      for (let group in context.groups) {
        groups[group] = !!props.microservice.groups.find(g => g === group);
      }
    } else {
      for (let group of context.groups) {
        groups[group] = !!props.microservice.groups.find(g => g === group);
      }
    }
    setGPlaceholder(Object.keys(groups).filter(group => groups[group]).join(', '));
    setGroups(groups);

    setModal(true);
  }

  function onDeleteMicroservice() {
    if (!window.confirm('The microservice ' + props.id + ' is going to be deleted.')) return;

    let config = {
      data: {id: props.id}
    };
    if (isGroupAdmin()) config.data.group = props.group;
    _axios.delete(dbPath, config)
      .then(response => {
        dispatch(deleteMicroservice({
          id: props.id
        }));
      })
      .catch(handleError);
  }

  function saveChanges(event) {
    event.stopPropagation();
    const newGroups = Object.keys(groups).filter(group => groups[group]);
    const newVersion = Object.keys(versions).find(version => versions[version]);
    let newMicroservice = {id: props.id};

    // validate groups and version
    if (newVersion.length === 0) {
      setMultiselectError('Select at least one version');
      return;
    }
    if (newGroups.length === 0) {
      setGError('Select at least one group');
      return;
    }

    if (JSON.stringify(newGroups) !== JSON.stringify(props.microservice.groups)) {
      newMicroservice.groups = newGroups;
    }
    if (newVersion !== props.microservice.version) {
      const {path} = microservice.versions.find(v => v.name === newVersion);
      const url = microservice.path + path;

      _axios.get(url)
        .then(response => {  // GET microservice/api/v1
          const {ok, data} = response.data;
          if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The api version ' + url + ' does not correspond to our standard.');
          if (ok !== 1) throw new Error('The api version ' + url + ' is currently unavailable.');
          if (data.length === 0) throw new Error('The api version ' + url + ' has no available fetcher.');

          newMicroservice.fetchers = data.map(fetcher => ({status: DEFAULT_STATUS, ...fetcher}));
          newMicroservice.version = newVersion;
          newMicroservice.uri = url;

          return newMicroservice;
        })
        .then(editMicroservice)
        .catch(handleErrorOnEdit);
    } else {
      editMicroservice(newMicroservice);
    }
  }

  function editMicroservice(newMicroservice) {
    if (isGroupAdmin()) newMicroservice.group = context.defaultGroup;
    _axios.put(context.role.MICROSERVICES_PATH, newMicroservice)
      .then(response => {
        // TODO validate response

        dispatch(updateMicroservice({
          newId: response.data.data.id,
          ...newMicroservice
        }));
        setModal(false);
      })
      .catch(handleError);
  }

  function onCloseModal(event) {
    event.stopPropagation();
    setVersions({});
    setModal(false);
    setPlaceholder('');
    setMultiselectError('');

    setGroups({});
    setGPlaceholder('');
    setGError('');
  }

  function cancel(event) {
    event.stopPropagation();
    setVersions({});
    setModal(false);
    setPlaceholder('');
    setMultiselectError('');
  }

  function handleErrorOnEdit(error) {
    let message;
    if (error.response) message = 'Internal server error';
    else if (error.request) message = 'No response received';
    else if (error.message) message = error.message;
    else message = 'Unknown error';
    setMultiselectError(message);
  }

  useEffect(() => {
    const username = _kc.tokenParsed.preferred_username;

    function isAdmin() {
      return props.group === DEFAULT_GROUP;
    }

    function isGroupAdmin() {
      return props.group !== undefined && props.group !== DEFAULT_GROUP;
    }

    function isRegularUser() {
      return props.group === undefined;
    }

    function isOwner() {  // only for regular users
      return props.microservice.author === username && props.microservice.groups === null;
    }

    setDeletePermission(isAdmin() || isGroupAdmin() || (isRegularUser() && isOwner()));
  }, [props]);

  return (
    <>
      <table className={styles.table} cellSpacing='0'>
        <thead>
          <tr>
            <th>
              <div className={styles.icon_wrapper} onClick={onChangeMicroserviceStatus}>
                <img src={props.microservice.status === -1 ? circleJellyBean : props.microservice.status === 0 ? circleCultured : circleOceanGreen} alt='Circle solid'/>
              </div>
            </th>
            <th colSpan='3'>
              <div>
                <div className={styles.icon_wrapper} onClick={onEditMicroservice}>
                  <img src={editImg} alt='Edit Solid Cultured 50'/>
                </div>
                {deletePermission &&
                  <div className={styles.icon_wrapper} onClick={onDeleteMicroservice}>
                    <img src={trashImg} alt='Trash Alt Solid Cultured 50'/>
                  </div>
                }
              </div>
              <div>
                <h3 title={props.microservice.uri}>{props.microservice.name}</h3>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>status</td>
            <td>fetcher</td>
            <td>description</td>
            <td>types</td>
          </tr>

          {props.microservice.fetchers.map((fetcher, index) => (
            <tr key={index} onClick={() => onChangeFetcherStatus(index)}>
              <td><img src={fetcher.status === -1 ? circleJellyBean : fetcher.status === 0 ? circleCultured : circleOceanGreen} alt='Circle solid'/></td>
              <td>{fetcher.name}</td>
              <td>{fetcher.info}</td>
              <td>
                {Object.keys(fetcher.types).sort().map(type => (
                  <Icon key={type} type={type}/>
                ))}
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            <td/>
            <td colSpan='2'>Added by {props.microservice.author} at {getDateTime(props.microservice.created_at)}</td>
            <td>Updated at {getDateTime(props.microservice.updated_at)}</td>
          </tr>
        </tfoot>
      </table>

      <Modal
        isOpen={modal}
        onRequestClose={onCloseModal}
        style={customStyles}
      >
        <div className={styles.modal}>
          <div className={styles.close} onClick={onCloseModal}/>

          <div>
            <p>NAME</p>
            <p>{props.microservice.name}</p>
          </div>
          <div>
            <p>HOST</p>
            <p>{props.microservice.host}</p>
          </div>

          <div className={styles.edit}>
            {!isRegularUser() &&
              <Select
                className={styles.input}
                placeholder={gPlaceholder ? gPlaceholder : 'Enter the group'}
                setPlaceholder={setGPlaceholder}
                items={groups}
                setItems={setGroups}
                error={gError}
                setError={setGError}
              />
            }
            <Select
              className={styles.input}
              singleSelect={true}
              placeholder={placeholder ? placeholder : 'Enter the version'}
              setPlaceholder={setPlaceholder}
              items={versions}
              setItems={setVersions}
              error={multiselectError}
              setError={setMultiselectError}
            />
          </div>

          <div className={styles.buttons}>
            <button className={buttonStyles.button} onClick={saveChanges}>save</button>
            <button className={buttonStyles.button} onClick={cancel}>cancel</button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Table;